import styled from "styled-components/macro";
import { Icon, ThumbsUpFilled, Info, ExclamationPointCircle } from "./Icons";
import { THEME_COLOR, ERROR_COLOR, WARNING_COLOR } from "../vars";
import Title from "./Title";

export type TLintRule = {
  label: string;
  type: "good" | "bad" | "info";
  description: string;
  matches: boolean;
};

export default function MessageLinterRule({ rule }: { rule: TLintRule }) {
  return (
    <Rule>
      <Title>
        {rule.label}
        <div>
          <small>{rule.description}</small>
        </div>
      </Title>
      {
        {
          good: <ThumbsUpFilled style={{ color: THEME_COLOR }} />,
          info: <Info style={{ color: WARNING_COLOR }} />,
          bad: <ExclamationPointCircle style={{ color: ERROR_COLOR }} />,
        }[rule.type]
      }
    </Rule>
  );
}

type Keyword = {
  replacement: string;
};

export function lintMessage(
  message: string,
  { keywords = [] }: { keywords?: Keyword[] } = {}
): TLintRule[] {
  const emojiRegex = /(\u00a9|\u00ae|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff])/;

  const capsRegex = /[A-Z]{3,}/;
  const triggerWordsRegex = /(BUY)|(SALE)|(PROMO)/i;
  const bangRegex = /!{3,}/;
  const urlRegex = new RegExp(
    "([a-zA-Z0-9]+://)?([a-zA-Z0-9_]+:[a-zA-Z0-9_]+@)?([a-zA-Z0-9.-]+\\.[A-Za-z]{2,4})(:[0-9]+)?(/.*)?"
  );

  const defaultRules: TLintRule[] = [
    {
      label: "May have excessive capitalization",
      type: "bad",
      description:
        "Words in ALL CAPS might be too harsh for a text and discourage responses.",
      matches: capsRegex.test(message),
    },
    {
      label: "Several exclamation points",
      type: "bad",
      description: "Don’t do it!!!!! It looks like spam!!!!!",
      matches: bangRegex.test(message),
    },
    {
      label: "Salesy",
      type: "bad",
      description:
        "Sometimes sales words cause phone carriers to block your messages.",
      matches: triggerWordsRegex.test(message),
    },
    {
      label: "Includes a link",
      type: "info",
      description:
        "Try to use links only in established conversations, when possible.",
      matches: urlRegex.test(message),
    },
    {
      label: "Personalization",
      type: "good",
      description:
        "Personalized messages use Skipio’s [merge tags] and will lead to better responses",
      matches: keywords
        .map((x) => x.replacement)
        .some((keyword) => message.includes(keyword)),
    },
    {
      label: "Questions",
      type: "good",
      description: "Asking questions encourages your contacts to respond.",
      matches: message.includes("?"),
    },
    {
      label: "Has an emoji",
      type: "good",
      description: "Emojis bring your personality into your messages",
      matches: emojiRegex.test(message),
    },
  ];

  return defaultRules.filter((rule) => rule.matches);
}

const Rule = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  min-height: 60px;
  padding: 10px 16px;
  padding-right: 0;
  ${Icon} {
    margin-right: 10px;
    width: 24px;
    height: 24px;
  }
`;
